import {
  type AddExistingCategoryFormValues,
  useAddExistingCategoryForm,
} from "./useAddExistingCategoryForm";
import { type AlmanacGroup } from "portal/components/categoryTemplates/useAlmanacOptions";
import { AlmanacMostSimilarFields } from "../AlmanacMostSimilarFields";
import { categoryHasAlmanac } from "portal/utils/categoryCollection/category";
import { CreateCategoryDialogActions } from "portal/components/categoryCollectionProfile/createCategory/CreateCategoryDialogActions";
import { Form, Formik, useFormikContext } from "formik";
import { PlantProfileCategoryOptionsField } from "portal/components/categoryCollectionProfile/form/PlantProfileCategoryOptionsField";
import { Skeleton } from "@mui/material";
import { usePlantProfileCategoryOptions } from "portal/components/categoryCollectionProfile/form/usePlantProfileCategoryOptions";
import { useTranslation } from "react-i18next";
import React, { FC } from "react";

type ExistingCategoryTemplateStepProps = {
  customerUuid?: string;
  onBack: () => void;
  onCancel: () => void;
  onSubmitSuccess: (newCategoryId: string) => void;
};

export const ExistingCategoryTemplateStep: FC<
  ExistingCategoryTemplateStepProps
> = ({ customerUuid, onBack, onCancel, onSubmitSuccess }) => {
  const { t } = useTranslation();
  const { almanacGroups, defaultAlmanacId, formConfig, isLoading } =
    useAddExistingCategoryForm(customerUuid, onSubmitSuccess);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton variant="rectangular" className="h-14 w-full rounded" />
      </div>
    );
  }

  return (
    <Formik key={customerUuid ?? ""} {...formConfig}>
      {({ submitForm, isSubmitting }) => (
        <Form>
          <AddExistingCategoryFields
            almanacGroups={almanacGroups}
            customerUuid={customerUuid}
            defaultAlmanacId={defaultAlmanacId}
          />
          <CreateCategoryDialogActions
            onBack={onBack}
            onCancel={onCancel}
            onConfirm={submitForm}
            confirmLabel={t("utils.actions.save")}
            confirmLoading={isSubmitting}
            disabled={isSubmitting}
          />
        </Form>
      )}
    </Formik>
  );
};

interface AddExistingCategoryFieldsProps {
  almanacGroups: AlmanacGroup[];
  customerUuid?: string;
  defaultAlmanacId: string;
}

const AddExistingCategoryFields: FC<AddExistingCategoryFieldsProps> = ({
  almanacGroups,
  customerUuid,
  defaultAlmanacId,
}) => {
  const { t } = useTranslation();
  const { values } = useFormikContext<AddExistingCategoryFormValues>();

  const {
    categoryOptions,
    isLoading: areCategoriesLoading,
    loadMore: loadMoreCategories,
    onSearchInputChange: onCategorySearchInputChange,
  } = usePlantProfileCategoryOptions(customerUuid, { refetchOnMount: true });

  const needsAlmanacSelection =
    values.sourceCategory !== null &&
    !categoryHasAlmanac(values.sourceCategory);

  return (
    <div className="flex flex-col gap-4">
      <PlantProfileCategoryOptionsField
        name="sourceCategory"
        label={t("components.categoryTemplates.fields.existingCategory")}
        categoryOptions={categoryOptions}
        loading={areCategoriesLoading}
        onLoadMore={loadMoreCategories}
        onSearchInputChange={onCategorySearchInputChange}
      />
      {needsAlmanacSelection ? (
        <AlmanacMostSimilarFields
          almanacGroups={almanacGroups}
          defaultAlmanacId={defaultAlmanacId}
        />
      ) : undefined}
    </div>
  );
};
